import { useCallback, useRef, useState } from 'react';

import type { ImportViolation } from '@/lib/api/generated/model';

import type { DraftSource } from '../builder/draftReducer';
import { toReplaceRequest, toSaveRequest } from '../builder/toRequest';
import { useDraft } from '../builder/useDraftContext';
import { isStepsInUse, isWorkflowRejection } from '../errors';
import {
  useDiscardPendingChanges,
  usePublishPendingChanges,
  useSavePendingChanges,
} from './usePendingChanges';
import { useReplaceWorkflow } from './useReplaceWorkflow';

/**
 * Every write the builder makes, and the one account of how the last one went.
 *
 * Lifted out of the header (spec 144) because Save is no longer only the
 * header's: the leave dialog's "Save draft and leave" is the same save, and has
 * to report a refusal exactly where Save does — the sentence in the header and
 * the located problems in the inspector — or a refused leave would fail in
 * silence.
 *
 * Each write resolves to whether it succeeded rather than throwing, so a caller
 * that only needs to know whether to go on (the leave dialog, a publish that
 * saves first) cannot forget to catch. The reason is already on screen.
 */
export function useDraftWrites({
  workspaceId,
  workflowId,
  onProblems,
}: {
  workspaceId: string;
  workflowId: string;
  /** Refusal details, lifted so the inspector can make each one selectable. */
  onProblems: (violations: ImportViolation[]) => void;
}) {
  const { draft, saveSucceeded } = useDraft();
  const replace = useReplaceWorkflow(workspaceId);
  const savePending = useSavePendingChanges(workspaceId);
  const publishPending = usePublishPendingChanges(workspaceId);
  const discardPending = useDiscardPendingChanges(workspaceId);
  const [failure, setFailure] = useState<Error | null>(null);
  // The save under way, if any. A second Save — the button and the leave
  // dialog both ask — joins it rather than starting another, which would only
  // be refused as stale by the first one's success.
  const inFlight = useRef<Promise<DraftSource | null> | null>(null);

  /** Runs one write; resolves to what it returned, or `null` if refused. */
  const attempt = useCallback(
    async (write: () => Promise<DraftSource>): Promise<DraftSource | null> => {
      onProblems([]);
      setFailure(null);
      try {
        const result = await write();
        saveSucceeded(result);
        return result;
      } catch (refusal) {
        const error =
          refusal instanceof Error
            ? refusal
            : new Error('The workflow could not be saved.');
        setFailure(error);
        // Located problems go to the inspector, where each one can select the
        // element it is about. Everything else is a sentence in the header.
        onProblems(
          isWorkflowRejection(error) || isStepsInUse(error)
            ? error.violations
            : [],
        );
        return null;
      }
    },
    [onProblems, saveSucceeded],
  );

  const saveNow = useCallback((): Promise<DraftSource | null> => {
    if (inFlight.current) return inFlight.current;

    const request = toSaveRequest(draft);
    const running = attempt(() =>
      request.door === 'replace'
        ? replace.mutateAsync({ workflowId, body: request.body })
        : savePending.mutateAsync({ workflowId, body: request.body }),
    ).finally(() => {
      inFlight.current = null;
    });
    inFlight.current = running;
    return running;
  }, [attempt, draft, replace, savePending, workflowId]);

  /** Save the draft where its state says (plan, "Reading and writing"). */
  const save = useCallback(
    (): Promise<boolean> => saveNow().then((saved) => saved !== null),
    [saveNow],
  );

  /** Publish a workflow never published, as today (FR-012). */
  const publish = useCallback(
    (): Promise<boolean> =>
      attempt(() =>
        replace.mutateAsync({
          workflowId,
          body: toReplaceRequest(draft, 'published'),
        }),
      ).then((published) => published !== null),
    [attempt, draft, replace, workflowId],
  );

  /**
   * Put a published workflow's draft live (FR-004).
   *
   * Unsaved edits are saved first: what goes live is what is on screen, and
   * publishing only the last saved draft beneath them would put something
   * live that the author is no longer looking at. The publish then quotes the
   * revision that save returned.
   */
  const publishChanges = useCallback(async (): Promise<boolean> => {
    let saved: DraftSource | null = null;
    if (draft.dirty || inFlight.current) {
      saved = await saveNow();
      if (!saved) return false;
    }
    const before = draft.pending?.revision ?? null;

    // Inside the attempt, so a publish with nothing to quote is reported where
    // every other refusal is, rather than doing nothing in silence.
    const published = await attempt(() => {
      const revision = saved ? revisionSaved(saved) : before;
      if (!revision) throw new Error(NOTHING_TO_PUBLISH);
      return publishPending.mutateAsync({ workflowId, revision });
    });
    return published !== null;
  }, [attempt, draft, publishPending, saveNow, workflowId]);

  /**
   * Throw a published workflow's draft away (FR-006), and with it any unsaved
   * edits on top: the builder shows the published version, clean.
   */
  const discardDraft = useCallback(async (): Promise<boolean> => {
    const revision = draft.pending?.revision;
    if (!revision) return false;
    const discarded = await attempt(() =>
      discardPending.mutateAsync({ workflowId, revision }),
    );
    return discarded !== null;
  }, [attempt, discardPending, draft, workflowId]);

  return {
    save,
    publish,
    publishChanges,
    discardDraft,
    failure,
    isPending:
      replace.isPending ||
      savePending.isPending ||
      publishPending.isPending ||
      discardPending.isPending,
  };
}

export type DraftWrites = ReturnType<typeof useDraftWrites>;

const NOTHING_TO_PUBLISH =
  'There is no saved draft to publish. Save your changes, then publish them.';

/**
 * The revision a save of a published workflow's draft returned.
 *
 * Such a save always answers with its pending changes; one that did not would
 * leave the publish quoting nothing. Said, rather than carried on as `null`.
 */
function revisionSaved(saved: DraftSource): string {
  const revision =
    'workflow' in saved ? saved.pending_changes?.revision : undefined;
  if (!revision) {
    throw new Error(
      'The draft was saved, but the service did not say which revision it is. Reload this workflow, then publish.',
    );
  }
  return revision;
}
